import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  FiFileText, 
  FiShield, 
  FiAlertTriangle,
  FiCalendar
} from 'react-icons/fi';

const Terms = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      id: 'terms',
      title: 'Terms of Service',
      icon: FiFileText,
      content: {
        effectiveDate: 'August 7, 2025',
        description: 'Welcome to Bervin, an AI-powered application designed to assist users with generating responses, content, or general information based on input queries.',
        agreement: 'By using Bervin, you agree to the following terms:',
        subsections: [
          {
            title: '1. Use of Service',
            items: [
              'You agree to use Bervin only for lawful purposes.',
              'You may not use the app to generate harmful, misleading, illegal, or discriminatory content.',
              'You are responsible for any content you input into the app.'
            ]
          },
          {
            title: '2. Intellectual Property',
            items: [
              'The underlying technology, branding, and design of Bervin are the property of its developers.',
              'You retain rights to your input and generated output unless otherwise stated.'
            ]
          },
          {
            title: '3. No Warranties',
            items: [
              'Bervin is provided "as-is" without warranties of any kind.',
              'We do not guarantee that the service will be uninterrupted or error-free.'
            ]
          },
          {
            title: '4. Limitation of Liability',
            items: [
              'We are not responsible for any damages arising from the use or inability to use Bervin.'
            ]
          },
          {
            title: '5. Termination',
            items: [
              'We reserve the right to suspend or terminate access if the service is misused.'
            ]
          }
        ]
      }
    },
    {
      id: 'privacy',
      title: 'Privacy Policy',
      icon: FiShield,
      content: {
        effectiveDate: 'August 7, 2025',
        description: 'Bervin respects your privacy. This policy outlines what data we collect and how we use it.',
        subsections: [
          {
            title: '1. Data Collection',
            items: [
              'Usage data (e.g., time, frequency of use)',
              'User queries and generated outputs (for performance monitoring and improvements)'
            ],
            description: 'We may collect:'
          },
          {
            title: '2. Use of Data',
            items: [
              'To improve AI responses and app performance',
              'To detect and prevent misuse'
            ],
            description: 'Collected data is used:'
          },
          {
            title: '3. Data Sharing',
            items: [
              'We do not sell or share your data with third parties.',
              'We use third-party services to generate responses.'
            ]
          },
          {
            title: '4. Data Security',
            items: [
              'All data is transmitted securely.',
              'Data may be stored temporarily for debugging or performance monitoring.'
            ]
          },
          {
            title: '5. Children\'s Privacy',
            items: [
              'Bervin is not intended for children under 13.',
              'We do not knowingly collect data from minors.'
            ]
          }
        ]
      }
    },
    {
      id: 'disclaimer',
      title: 'Disclaimer',
      icon: FiAlertTriangle,
      content: {
        description: 'Important information about using Bervin AI',
        items: [
          'Bervin is an experimental AI tool and should be used with caution.',
          'Bervin does not provide professional advice (medical, legal, financial, or otherwise).',
          'Users should not rely on the app for decisions that can have significant personal, professional, or legal consequences.',
          'All outputs are generated by artificial intelligence and may contain errors or inaccuracies.'
        ]
      }
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-900 via-dark-800 to-secondary-900 text-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-full mb-8"
            >
              <FiFileText className="w-10 h-10 text-white" />
            </motion.div>
            
            <h1 className="text-5xl md:text-6xl font-display font-bold mb-6">
              <span className="gradient-text">Legal Information</span>
            </h1>
            
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Our commitment to transparency, privacy, and responsible AI usage
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto space-y-16">
          {sections.map((section, sectionIndex) => (
            <motion.div
              key={section.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: sectionIndex * 0.2 }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-secondary-500/10 to-accent-500/10 rounded-3xl blur-xl"></div>
              <div className="relative bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-3xl p-8 md:p-12">
                
                {/* Section Header */}
                <div className="flex items-center mb-8">
                  <div className="p-4 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-xl mr-6">
                    <section.icon className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h2 className="text-3xl md:text-4xl font-display font-bold gradient-text mb-2">
                      {section.title}
                    </h2>
                    {section.content.effectiveDate && (
                      <div className="flex items-center text-gray-400">
                        <FiCalendar className="w-4 h-4 mr-2" />
                        <span>Effective Date: {section.content.effectiveDate}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Section Content */}
                <div className="space-y-8">
                  {section.content.description && (
                    <p className="text-gray-300 text-lg leading-relaxed">
                      {section.content.description}
                    </p>
                  )}

                  {section.content.agreement && (
                    <p className="text-gray-300 text-lg leading-relaxed font-medium">
                      {section.content.agreement}
                    </p>
                  )}

                  {/* Direct items (for disclaimer) */}
                  {section.content.items && (
                    <div className="space-y-4">
                      {section.content.items.map((item, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.6, delay: sectionIndex * 0.2 + index * 0.1 }}
                          className="flex items-start space-x-4"
                        >
                          <div className="w-2 h-2 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-full mt-3 flex-shrink-0"></div>
                          <p className="text-gray-300 leading-relaxed">{item}</p>
                        </motion.div>
                      ))}
                    </div>
                  )}

                  {/* Subsections (for terms and privacy) */}
                  {section.content.subsections && (
                    <div className="space-y-8">
                      {section.content.subsections.map((subsection, subIndex) => (
                        <motion.div
                          key={subIndex}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.6, delay: sectionIndex * 0.2 + subIndex * 0.1 }}
                          className="border-l-2 border-gradient-to-b from-secondary-500 to-accent-500 pl-6"
                        >
                          <h3 className="text-xl font-semibold text-white mb-3">
                            {subsection.title}
                          </h3>
                          
                          {subsection.description && (
                            <p className="text-gray-300 mb-3">{subsection.description}</p>
                          )}
                          
                          <div className="space-y-3">
                            {subsection.items.map((item, itemIndex) => (
                              <div key={itemIndex} className="flex items-start space-x-3">
                                <div className="w-1.5 h-1.5 bg-secondary-400 rounded-full mt-2 flex-shrink-0"></div>
                                <p className="text-gray-300 leading-relaxed">{item}</p>
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-center"
          >
            <div className="bg-gradient-to-r from-dark-800/50 to-secondary-900/50 backdrop-blur-sm border border-white/10 rounded-3xl p-12">
              <h3 className="text-3xl font-display font-bold mb-6 gradient-text">
                Questions About Our Terms?
              </h3>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                If you have any questions about these terms, privacy policy, or disclaimer, 
                please don't hesitate to contact us.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
                <a
                  href="mailto:bervin.helpdesk@gmail.com"
                  className="px-8 py-4 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-xl font-semibold text-white text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
                >
                  Contact Support
                </a>
                <a
                  href="/contact"
                  className="px-8 py-4 border border-white/20 rounded-xl font-semibold text-white text-lg hover:bg-white/10 transition-all duration-300"
                >
                  Visit Contact Page
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Terms;
